import { useEffect, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import useUiStore from '@/shared/store/ui.store'
import useAudioStore from '@/shared/store/audio.store'
import useTracksStore from '@/shared/store/tracks.store'

const PLAYBACK_LEFT_PADDING = 16

export const usePlaybackPosition = (): {
  position: number
  handleClick: (clientX: number, rectLeft: number) => void
  handleDrag: (startX: number, initial: number) => (moveX: number) => void
  PLAYBACK_LEFT_PADDING: number
} => {
  const [position, setPosition] = useState(PLAYBACK_LEFT_PADDING)

  const pixelsPerSecond = useUiStore((state) => state.pixelsPerSecond)

  const { playbackTime, setPlaybackTime, maxProjectDuration } = useTracksStore(
    useShallow((state) => ({
      playbackTime: state.playbackTime,
      setPlaybackTime: state.setPlaybackTime,
      maxProjectDuration: state.maxProjectDuration,
    }))
  )

  const { pausePlayback, currentAudioId } = useAudioStore(
    useShallow((state) => ({
      pausePlayback: state.pausePlayback,
      currentAudioId: state.currentAudioId,
    }))
  )

  useEffect(() => {
    const max = PLAYBACK_LEFT_PADDING + pixelsPerSecond * maxProjectDuration
    const pos = PLAYBACK_LEFT_PADDING + pixelsPerSecond * playbackTime
    const clamped = Math.max(PLAYBACK_LEFT_PADDING, Math.min(pos, max))
    setPosition(clamped)
  }, [playbackTime, currentAudioId, pixelsPerSecond, maxProjectDuration])

  const handleClick = (clientX: number, rectLeft: number): void => {
    pausePlayback()
    const px = clientX - rectLeft - 1
    setPlaybackTime(Math.max(0, px / pixelsPerSecond))
  }

  const handleDrag = (startX: number, initial: number) => {
    pausePlayback()
    const max = PLAYBACK_LEFT_PADDING + pixelsPerSecond * maxProjectDuration

    return (moveX: number) => {
      const delta = moveX - startX
      const next = Math.max(
        PLAYBACK_LEFT_PADDING,
        Math.min(initial + delta, max)
      )
      setPosition(next)
      setPlaybackTime((next - PLAYBACK_LEFT_PADDING) / pixelsPerSecond)
    }
  }

  return {
    position,
    handleClick,
    handleDrag,
    PLAYBACK_LEFT_PADDING,
  }
}
